import {
  Entity,
  RELATION_CONSUMES_API,
  RELATION_DEPENDS_ON,
  RELATION_PROVIDES_API,
} from '@backstage/catalog-model';
import { useEntity } from '@backstage/plugin-catalog-react';
import { NAME_MIN_WIDTH } from '../shared/table';
import { useByxT } from '../shared/translations';
import { ByxCardBoundary } from './ByxCardBoundary';
import {
  hasRelationItems,
  RelationColumns,
  RelationPanels,
  RelationSection,
} from './RelationPanels';

/** The three lists of the tab, in order, with the relation behind each. */
export const DEPENDENCY_SECTIONS: ReadonlyArray<RelationSection> = [
  {
    id: 'provides',
    relation: RELATION_PROVIDES_API,
    titleKey: 'entity.dependencies.provides',
  },
  {
    id: 'consumes',
    relation: RELATION_CONSUMES_API,
    titleKey: 'entity.dependencies.consumes',
  },
  {
    id: 'dependsOn',
    relation: RELATION_DEPENDS_ON,
    titleKey: 'entity.dependencies.dependsOn',
  },
];

/** Column widths of the UI spec (Nome takes the rest, at least 320px). */
export const DEPENDENCY_COLUMNS = {
  owner: 160,
  type: 110,
  lifecycle: 120,
} as const;

/** The Dependências column plan, shared by the Componente API tab. */
export const DEPENDENCY_COLUMN_PLAN: RelationColumns = {
  name: NAME_MIN_WIDTH,
  ...DEPENDENCY_COLUMNS,
};

/** True when the component provides, consumes or depends on anything. */
export function hasDependencies(entity: Entity): boolean {
  return hasRelationItems(entity, DEPENDENCY_SECTIONS);
}

function DependenciesBody() {
  const { entity } = useEntity();
  const { t } = useByxT();
  // No all-empty message (D-13): the tab is hidden for a component without
  // dependencies (`hasDependencies`), and an empty section is hidden too.
  return (
    <RelationPanels
      entity={entity}
      title={t('entity.dependencies.title')}
      sections={DEPENDENCY_SECTIONS}
      columns={DEPENDENCY_COLUMN_PLAN}
    />
  );
}

/**
 * The Dependências tab (UI spec): Fornece (APIs), Consome (APIs) and
 * Depende de, each with its count, one line per entity (Nome with the
 * description, Dono by display name, Tipo, Ciclo de vida). Empty lists are
 * hidden, and so is the tab when all three are (D-13); a row opens its
 * entity.
 */
export function DependenciesContent() {
  const { t } = useByxT();
  return (
    <ByxCardBoundary title={t('entity.dependencies.title')} variant="table">
      <DependenciesBody />
    </ByxCardBoundary>
  );
}
